import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  TextField,
  Tabs,
  Tab
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  Tune as TuneIcon,
  Assignment as AssignmentIcon,
  Edit as EditIcon,
  Map as MapIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon
} from '@mui/icons-material';
import plantillasApi from '../../../api/plantillasApi';
import configuracionApi from '../../../api/configuracionApi';
import empresasApi from '../../../api/empresasApi';
import axios from 'axios';
import appConfig from '../../../config';

/**
 * Dialog para configurar una empresa (Configuración V1)
 * Permite al Admin Sistema configurar una empresa específica
 *
 * @param {Boolean} open - Si el dialog está abierto
 * @param {Object} empresa - Objeto con datos de la empresa
 * @param {Function} onClose - Callback al cerrar
 * @param {Function} onConfiguracionGuardada - Callback al guardar
 */
function DialogConfigurarEmpresa({ open, empresa, onClose, onConfiguracionGuardada }) {

  // Estados
  const [tabConfig, setTabConfig] = useState(0);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  // Estados para plantillas
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState('');
  const [loadingPlantillas, setLoadingPlantillas] = useState(true);

  // Estados para nombres personalizados
  const [nombresEditados, setNombresEditados] = useState({});

  // Estado para GIS
  const [soporteGIS, setSoporteGIS] = useState(false);

  // Estados para datos de empresa
  const [datosEmpresa, setDatosEmpresa] = useState({
    nombre_empresa: '',
    nro_doc_empresa: '',
    contacto_empresa: '',
    telefono_empresa: '',
    email_empresa: '',
    id_pais: '',
    is_active: true
  });
  const [paises, setPaises] = useState([]);

  // Estados de la empresa que se está configurando (NO del usuario admin)
  const [estadosEmpresa, setEstadosEmpresa] = useState([]);
  const [loadingEstados, setLoadingEstados] = useState(false);

  /**
   * Cargar configuración de la empresa cuando se abre el dialog
   */
  useEffect(() => {
    if (open && empresa) {
      cargarConfiguracion();
      cargarPlantillas();
      cargarPaises();
      // Cargar datos de la empresa
      if (empresa.id) {
        cargarDatosEmpresa();
        cargarEstadosEmpresa(empresa.id);
      }
    }
  }, [open, empresa]);

  /**
   * Actualizar estados cuando carga la config
   */
  useEffect(() => {
    if (config) {
      setNombresEditados(config.nombres_estados_custom || {});
      setSoporteGIS(config.soporte_gis || false);
      setPlantillaSeleccionada(config.id_plantilla || '');
    }
  }, [config]);

  /**
   * Cargar configuración de la empresa
   */
  const cargarConfiguracion = async () => {
    if (!empresa?.id) return;

    setLoading(true);
    try {
      const response = await configuracionApi.getConfiguracionByEmpresa(empresa.id);
      setConfig(response.data);
    } catch (error) {
      console.error('Error cargando configuración:', error);
      setMensaje({
        type: 'error',
        text: 'Error al cargar configuración de la empresa'
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cargar plantillas disponibles
   */
  const cargarPlantillas = async () => {
    try {
      setLoadingPlantillas(true);
      const response = await plantillasApi.getPlantillas();
      setPlantillas(response.data || []);
    } catch (error) {
      console.error('Error cargando plantillas:', error);
    } finally {
      setLoadingPlantillas(false);
    }
  };

  /**
   * Cargar países disponibles
   */
  const cargarPaises = async () => {
    try {
      const response = await axios.get(`${appConfig.API_BASE_URL}/api/paises`);
      setPaises(response.data || []);
    } catch (error) {
      console.error('Error cargando países:', error);
    }
  };

  /**
   * Cargar datos de la empresa
   */
  const cargarDatosEmpresa = async () => {
    try {
      const response = await empresasApi.obtener(empresa.id);
      setDatosEmpresa(response.data);
    } catch (error) {
      console.error('Error cargando datos de empresa:', error);
    }
  };

  /**
   * Cargar estados activos de la empresa específica que se está configurando
   * IMPORTANTE: Carga estados de la empresa objetivo, NO del usuario admin
   */
  const cargarEstadosEmpresa = async (idEmpresa) => {
    if (!idEmpresa) return;

    setLoadingEstados(true);
    try {
      const user = JSON.parse(localStorage.getItem('user'));

      // Llamar al endpoint con id_empresa específico de la empresa que se configura
      const response = await axios.get(
        `${appConfig.API_BASE_URL}/api/v1/configuracion/estados-activos`,
        {
          headers: { Authorization: `Bearer ${user.token}` },
          params: { id_empresa: idEmpresa }  // ← ID de la empresa objetivo, NO del admin
        }
      );

      console.log('📊 Estados cargados para empresa ID', idEmpresa, ':', response.data);

      if (response.data.success) {
        const estadosData = (response.data.data || []).map(estado => ({
          ...estado,
          // Protección: si nombre está vacío, usar nombre_default
          nombre: estado.nombre && estado.nombre.trim() !== '' ? estado.nombre : estado.nombre_default
        }));

        console.log('✅ Estados procesados:', estadosData);
        setEstadosEmpresa(estadosData);
      }
    } catch (error) {
      console.error('❌ Error cargando estados de empresa:', error);
      setEstadosEmpresa([]);
    } finally {
      setLoadingEstados(false);
    }
  };

  /**
   * Guardar datos de la empresa
   */
  const handleGuardarDatosEmpresa = async () => {
    if (!empresa?.id) return;

    setProcesando(true);
    try {
      await empresasApi.actualizar(empresa.id, datosEmpresa);

      setMensaje({
        type: 'success',
        text: 'Datos de la empresa actualizados correctamente'
      });

      if (onConfiguracionGuardada) {
        onConfiguracionGuardada();
      }
    } catch (error) {
      console.error('Error guardando datos de empresa:', error);
      setMensaje({
        type: 'error',
        text: error.response?.data?.error || 'Error al guardar los datos de la empresa'
      });
    } finally {
      setProcesando(false);
    }
  };

  /**
   * Aplicar plantilla a la empresa
   */
  const handleAplicarPlantilla = async () => {
    if (!plantillaSeleccionada || !empresa?.id) return;

    setProcesando(true);
    try {
      // Usar el método correcto para Admin Sistema
      await configuracionApi.aplicarPlantillaByEmpresa(empresa.id, plantillaSeleccionada);

      setMensaje({
        type: 'success',
        text: 'Plantilla aplicada correctamente'
      });

      // Recargar configuración Y estados activos
      await cargarConfiguracion();
      await cargarEstadosEmpresa(empresa.id);

      // Callback de éxito
      if (onConfiguracionGuardada) {
        onConfiguracionGuardada();
      }
    } catch (error) {
      console.error('Error aplicando plantilla:', error);
      setMensaje({
        type: 'error',
        text: error.response?.data?.error || 'Error al aplicar plantilla'
      });
    } finally {
      setProcesando(false);
    }
  };

  /**
   * Guardar nombres personalizados
   */
  const handleGuardarNombres = async () => {
    if (!empresa?.id) return;

    setProcesando(true);
    try {
      await configuracionApi.personalizarEstadosByEmpresa(empresa.id, nombresEditados);

      setMensaje({
        type: 'success',
        text: 'Nombres de estados personalizados correctamente'
      });

      // Recargar configuración Y estados activos
      await cargarConfiguracion();
      await cargarEstadosEmpresa(empresa.id);

      if (onConfiguracionGuardada) {
        onConfiguracionGuardada();
      }
    } catch (error) {
      console.error('Error personalizando nombres:', error);
      setMensaje({
        type: 'error',
        text: error.response?.data?.error || 'Error al personalizar nombres'
      });
    } finally {
      setProcesando(false);
    }
  };

  /**
   * Guardar configuración GIS
   */
  const handleGuardarGIS = async () => {
    if (!empresa?.id) return;

    setProcesando(true);
    try {
      await configuracionApi.configurarGISByEmpresa(empresa.id, soporteGIS);

      setMensaje({
        type: 'success',
        text: `GIS ${soporteGIS ? 'activado' : 'desactivado'} correctamente`
      });

      // Recargar configuración
      await cargarConfiguracion();

      if (onConfiguracionGuardada) {
        onConfiguracionGuardada();
      }
    } catch (error) {
      console.error('Error configurando GIS:', error);
      setMensaje({
        type: 'error',
        text: error.response?.data?.error || 'Error al configurar GIS'
      });
    } finally {
      setProcesando(false);
    }
  };

  /**
   * Cambiar nombre de un estado
   */
  const handleChangeNombre = (idEstado, nuevoNombre) => {
    setNombresEditados(prev => ({
      ...prev,
      [idEstado]: nuevoNombre
    }));
  };

  /**
   * Obtener nombre de estado (custom o default desde BD)
   * Usa estadosEmpresa (estados de la empresa que se está configurando)
   */
  const getNombreEstado = (idEstado) => {
    // Buscar el estado en estadosEmpresa (NO en EstadosContext)
    const estado = estadosEmpresa.find(e => e.id === parseInt(idEstado));

    if (!estado) {
      return `Estado ${idEstado}`;
    }

    // Si hay nombre personalizado y no está vacío, usarlo
    if (estado.nombre && estado.nombre.trim() !== '') {
      return estado.nombre;
    }

    // Sino, usar nombre_default de tp_estado_servicio
    return estado.nombre_default || `Estado ${idEstado}`;
  };

  /**
   * Obtener nombre default de un estado desde BD (tp_estado_servicio)
   * Usa estadosEmpresa (estados de la empresa que se está configurando)
   */
  const getNombreDefault = (idEstado) => {
    const estado = estadosEmpresa.find(e => e.id === parseInt(idEstado));
    return estado?.nombre_default || `Estado ${idEstado}`;
  };

  /**
   * Cambiar tab
   */
  const handleChangeTab = (event, newValue) => {
    setTabConfig(newValue);
    setMensaje(null); // Limpiar mensajes al cambiar tab
  };

  /**
   * Cerrar dialog
   */
  const handleCerrar = () => {
    if (!procesando) {
      setTabConfig(0);
      setMensaje(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCerrar}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 12px 48px rgba(0, 0, 0, 0.2)'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ borderBottom: '2px solid #F5F5F5' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <TuneIcon sx={{ mr: 2, color: '#66BB6A', fontSize: 40 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Configuración Empresa
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {empresa?.nombre}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" gap={1}>
            <IconButton
              size="small"
              onClick={cargarConfiguracion}
              disabled={loading || procesando}
            >
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={handleCerrar} disabled={procesando}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 0 }}>
        {/* Mensajes */}
        {mensaje && (
          <Box px={3} pt={2}>
            <Alert
              severity={mensaje.type}
              onClose={() => setMensaje(null)}
            >
              {mensaje.text}
            </Alert>
          </Box>
        )}

        {/* Loading */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Tabs */}
            <Tabs
              value={tabConfig}
              onChange={handleChangeTab}
              sx={{
                borderBottom: '1px solid #E0E0E0',
                px: 3,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 'bold',
                  minHeight: 60
                }
              }}
            >
              <Tab
                label="Datos de la Empresa"
                icon={<BusinessIcon />}
                iconPosition="start"
              />
              <Tab
                label="Plantilla Aplicada"
                icon={<AssignmentIcon />}
                iconPosition="start"
              />
              <Tab
                label="Estados Personalizados"
                icon={<EditIcon />}
                iconPosition="start"
              />
              <Tab
                label="Soporte GIS"
                icon={<MapIcon />}
                iconPosition="start"
              />
            </Tabs>

            {/* Tab 0: Datos de la Empresa */}
            {tabConfig === 0 && (
              <Box p={3}>
                <Typography variant="h6" gutterBottom>
                  Información de la Empresa
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Edita los datos generales de la empresa
                </Typography>

                <Grid container spacing={3} sx={{ mt: 1, mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Nombre de la Empresa *"
                      value={datosEmpresa.nombre_empresa}
                      onChange={(e) => setDatosEmpresa({...datosEmpresa, nombre_empresa: e.target.value})}
                      disabled={procesando}
                      required
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="RUC / Número de Documento *"
                      value={datosEmpresa.nro_doc_empresa}
                      onChange={(e) => setDatosEmpresa({...datosEmpresa, nro_doc_empresa: e.target.value})}
                      disabled={procesando}
                      required
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Contacto"
                      value={datosEmpresa.contacto_empresa}
                      onChange={(e) => setDatosEmpresa({...datosEmpresa, contacto_empresa: e.target.value})}
                      disabled={procesando}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Teléfono"
                      value={datosEmpresa.telefono_empresa}
                      onChange={(e) => setDatosEmpresa({...datosEmpresa, telefono_empresa: e.target.value})}
                      disabled={procesando}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email de Contacto"
                      type="email"
                      value={datosEmpresa.email_empresa}
                      onChange={(e) => setDatosEmpresa({...datosEmpresa, email_empresa: e.target.value})}
                      disabled={procesando}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>País</InputLabel>
                      <Select
                        value={datosEmpresa.id_pais}
                        onChange={(e) => setDatosEmpresa({...datosEmpresa, id_pais: e.target.value})}
                        label="País"
                        disabled={procesando}
                      >
                        <MenuItem value="">
                          <em>Seleccione un país</em>
                        </MenuItem>
                        {paises.map((pais) => (
                          <MenuItem key={pais.id_pais} value={pais.id_pais}>
                            {pais.det_pais}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={datosEmpresa.is_active}
                          onChange={(e) => setDatosEmpresa({...datosEmpresa, is_active: e.target.checked})}
                          color={datosEmpresa.is_active ? 'success' : 'default'}
                          disabled={procesando}
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          {datosEmpresa.is_active ? <ToggleOnIcon color="success" /> : <ToggleOffIcon />}
                          <Box>
                            <Typography variant="body1" fontWeight="bold">
                              Estado de la Empresa
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {datosEmpresa.is_active
                                ? 'La empresa está ACTIVA y puede operar normalmente'
                                : 'La empresa está DESACTIVADA y no puede realizar operaciones'}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </Grid>
                </Grid>

                <Button
                  variant="contained"
                  onClick={handleGuardarDatosEmpresa}
                  disabled={procesando}
                  startIcon={procesando ? <CircularProgress size={20} /> : <CheckIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #66BB6A 0%, #4CAF50 100%)',
                    fontWeight: 'bold',
                    px: 4
                  }}
                >
                  {procesando ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </Box>
            )}

            {/* Tab 1: Plantilla */}
            {tabConfig === 1 && (
              <Box p={3}>
                {/* Plantilla actual */}
                {config?.id_plantilla ? (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Plantilla actual: <strong>{config.nombre_plantilla}</strong>
                  </Alert>
                ) : (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    No hay plantilla aplicada
                  </Alert>
                )}

                {/* Selector de plantilla */}
                <Typography variant="h6" gutterBottom>
                  Aplicar Plantilla
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Selecciona una plantilla para configurar automáticamente los estados de proceso
                </Typography>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Cambiar Plantilla</InputLabel>
                  <Select
                    value={plantillaSeleccionada}
                    onChange={(e) => setPlantillaSeleccionada(e.target.value)}
                    label="Cambiar Plantilla"
                    disabled={loadingPlantillas || procesando}
                  >
                    <MenuItem value="">
                      <em>Sin plantilla</em>
                    </MenuItem>
                    {plantillas.map(p => (
                      <MenuItem key={p.id_plantilla} value={p.id_plantilla}>
                        <Box>
                          <Typography variant="body1">{p.nombre_plantilla}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {p.descripcion} - {p.tipo_negocio}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  onClick={handleAplicarPlantilla}
                  disabled={!plantillaSeleccionada || procesando}
                  startIcon={procesando ? <CircularProgress size={20} /> : <CheckIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
                    mb: 3
                  }}
                >
                  {procesando ? 'Aplicando...' : 'Aplicar Plantilla'}
                </Button>

                {/* Estados activos actuales */}
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>
                  Estados Activos ({config?.estados_activos?.length || 0})
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {config?.estados_activos?.map(idEstado => (
                    <Chip
                      key={idEstado}
                      label={getNombreEstado(idEstado)}
                      color="primary"
                      icon={<CheckIcon />}
                    />
                  ))}
                  {(!config?.estados_activos || config.estados_activos.length === 0) && (
                    <Typography variant="body2" color="textSecondary">
                      No hay estados configurados
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Tab 2: Nombres Personalizados */}
            {tabConfig === 2 && (
              <Box p={3}>
                <Typography variant="h6" gutterBottom>
                  Personalizar Nombres de Estados
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Personaliza los nombres de los estados para esta empresa
                </Typography>

                <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
                  {config?.estados_activos?.map(idEstado => {
                    const nombreDefault = getNombreDefault(idEstado);
                    return (
                      <Grid item xs={12} md={6} key={idEstado}>
                        <TextField
                          fullWidth
                          label={nombreDefault}
                          value={nombresEditados[idEstado] || ''}
                          onChange={(e) => handleChangeNombre(idEstado, e.target.value)}
                          placeholder={nombreDefault}
                          helperText={`Por defecto: "${nombreDefault}"`}
                          disabled={procesando || loadingEstados}
                        />
                      </Grid>
                    );
                  })}
                  {(!config?.estados_activos || config.estados_activos.length === 0) && (
                    <Grid item xs={12}>
                      <Alert severity="warning">
                        No hay estados activos para personalizar. Aplica una plantilla primero.
                      </Alert>
                    </Grid>
                  )}
                </Grid>

                <Button
                  variant="contained"
                  onClick={handleGuardarNombres}
                  disabled={!config?.estados_activos || procesando}
                  startIcon={procesando ? <CircularProgress size={20} /> : <CheckIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #66BB6A 0%, #4CAF50 100%)'
                  }}
                >
                  {procesando ? 'Guardando...' : 'Guardar Nombres'}
                </Button>
              </Box>
            )}

            {/* Tab 3: GIS */}
            {tabConfig === 3 && (
              <Box p={3}>
                <Typography variant="h6" gutterBottom>
                  Configuración de Geolocalización (GIS)
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Habilita el soporte para coordenadas de geolocalización en las órdenes de servicio
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={soporteGIS}
                      onChange={(e) => setSoporteGIS(e.target.checked)}
                      color="primary"
                      disabled={procesando}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">
                        Habilitar soporte para coordenadas GIS
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {soporteGIS
                          ? 'Sistema de geolocalización activo'
                          : 'Sistema de geolocalización desactivado'}
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 3 }}
                />

                <Button
                  variant="contained"
                  onClick={handleGuardarGIS}
                  disabled={procesando}
                  startIcon={procesando ? <CircularProgress size={20} /> : <CheckIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #4FC3F7 0%, #29B6F6 100%)'
                  }}
                >
                  {procesando ? 'Guardando...' : 'Guardar Configuración GIS'}
                </Button>

                {/* Info adicional */}
                <Divider sx={{ my: 3 }} />
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    <strong>ID Empresa:</strong> {config?.id_empresa || empresa?.id}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" mt={1}>
                    <strong>Última actualización:</strong>{' '}
                    {config?.updated_at
                      ? new Date(config.updated_at).toLocaleString('es-PE', {
                          dateStyle: 'medium',
                          timeStyle: 'short'
                        })
                      : 'N/A'}
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ borderTop: '2px solid #F5F5F5', px: 3, py: 2 }}>
        <Button
          onClick={handleCerrar}
          disabled={procesando}
          color="inherit"
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DialogConfigurarEmpresa;
